import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useTheme, API } from "@/App";
import axios from "axios";
import { Link2, ExternalLink, Moon, Sun } from "lucide-react";

const themeMap = {
  purple: "#7c3aed",
  blue:   "#3b82f6",
  green:  "#10b981",
  pink:   "#ec4899",
  orange: "#f59e0b",
  red:    "#ef4444",
  teal:   "#14b8a6",
  indigo: "#6366f1",
};

// ============ TEMPLATE RENDERERS ============

const TemplateClassic = ({ microsite, color, onLinkClick }) => (
  <div className="max-w-md mx-auto px-4 py-12">
    <div className="text-center mb-8">
      <Avatar microsite={microsite} color={color} size="lg" />
      <Title microsite={microsite} />
    </div>
    <LinkList microsite={microsite} color={color} onLinkClick={onLinkClick} variant="rounded" />
    <Footer color={color} />
  </div>
);

const TemplateCard = ({ microsite, color, onLinkClick }) => (
  <div className="max-w-md mx-auto">
    {/* Cover Banner */}
    <div className="h-48 w-full relative"
      style={{
        background: microsite.cover_url
          ? `url(${microsite.cover_url}) center/cover`
          : `linear-gradient(135deg, ${color}, ${color}88)`
      }}>
      <div className="absolute -bottom-12 left-1/2 -translate-x-1/2">
        <Avatar microsite={microsite} color={color} size="lg" border />
      </div>
    </div>
    <div className="pt-16 px-4 pb-12 text-center">
      <Title microsite={microsite} />
      <LinkList microsite={microsite} color={color} onLinkClick={onLinkClick} variant="card" />
      <Footer color={color} />
    </div>
  </div>
);

const TemplateMinimal = ({ microsite, color, onLinkClick }) => (
  <div className="max-w-md mx-auto px-6 py-12">
    <div className="flex items-center gap-4 mb-8">
      <Avatar microsite={microsite} color={color} size="sm" />
      <div>
        <h1 className="text-xl font-bold" style={{ fontFamily: "Plus Jakarta Sans" }}>
          {microsite.title}
        </h1>
        {microsite.bio && (
          <p className="text-sm text-muted-foreground">{microsite.bio}</p>
        )}
      </div>
    </div>
    <div className="border-t border-border mb-6" />
    <LinkList microsite={microsite} color={color} onLinkClick={onLinkClick} variant="minimal" />
    <Footer color={color} />
  </div>
);

const TemplateBold = ({ microsite, color, onLinkClick }) => (
  <div className="min-h-screen"
    style={{
      background: microsite.background_url
        ? `url(${microsite.background_url}) center/cover fixed`
        : `linear-gradient(135deg, ${color} 0%, ${color}bb 100%)`
    }}>
    <div className="min-h-screen bg-black/30 backdrop-blur-sm">
      <div className="max-w-md mx-auto px-4 py-12 text-center">
        <Avatar microsite={microsite} color={color} size="lg" glassy />
        <h1 className="text-3xl font-bold text-white mt-4 mb-2"
          style={{ fontFamily: "Plus Jakarta Sans" }}>
          {microsite.title}
        </h1>
        {microsite.bio && (
          <p className="text-white/80 mb-8">{microsite.bio}</p>
        )}
        <LinkList microsite={microsite} color={color} onLinkClick={onLinkClick} variant="bold" />
        <Footer color={color} light />
      </div>
    </div>
  </div>
);

// ============ SHARED SUB-COMPONENTS ============

const Avatar = ({ microsite, color, size = "lg", border = false, glassy = false }) => {
  const sizeClass = size === "lg" ? "w-24 h-24 text-3xl" : "w-14 h-14 text-xl";
  const borderStyle = border ? { border: `4px solid white` } : {};
  const bg = glassy ? "rgba(255,255,255,0.2)" : color;

  return microsite.avatar_url ? (
    <img src={microsite.avatar_url} alt={microsite.title}
      className={`${sizeClass} rounded-full mx-auto object-cover shadow-xl`}
      style={borderStyle} />
  ) : (
    <div className={`${sizeClass} rounded-full mx-auto flex items-center justify-center font-bold text-white shadow-xl`}
      style={{ backgroundColor: bg, ...borderStyle }}>
      {microsite.title.charAt(0).toUpperCase()}
    </div>
  );
};

const Title = ({ microsite }) => (
  <div className="mt-4">
    <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: "Plus Jakarta Sans" }}>
      {microsite.title}
    </h1>
    {microsite.bio && (
      <p className="text-muted-foreground text-sm">{microsite.bio}</p>
    )}
  </div>
);

const LinkList = ({ microsite, color, onLinkClick, variant }) => {
  const variants = {
    rounded: (link, i) => (
      <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
        onClick={() => onLinkClick(i)}
        className="block group" data-testid={`microsite-link-${i}`}>
        <div className="p-4 rounded-2xl border-2 bg-card hover:scale-[1.02] transition-all duration-300 flex items-center justify-between"
          style={{ borderColor: `${color}33`, boxShadow: `0 4px 20px ${color}10` }}>
          <span className="font-medium" style={{ fontFamily: "Plus Jakarta Sans" }}>{link.title}</span>
          <ExternalLink className="w-5 h-5 transition-transform group-hover:translate-x-1" style={{ color }} />
        </div>
      </a>
    ),
    card: (link, i) => (
      <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
        onClick={() => onLinkClick(i)}
        className="block group" data-testid={`microsite-link-${i}`}>
        <div className="p-4 rounded-xl border bg-card/80 hover:bg-card hover:scale-[1.02] transition-all flex items-center justify-between mt-3"
          style={{ borderColor: `${color}44` }}>
          <span className="font-medium">{link.title}</span>
          <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" style={{ color }} />
        </div>
      </a>
    ),
    minimal: (link, i) => (
      <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
        onClick={() => onLinkClick(i)}
        className="flex items-center justify-between py-3 border-b border-border hover:pl-2 transition-all group"
        data-testid={`microsite-link-${i}`}>
        <span className="font-medium">{link.title}</span>
        <span className="text-muted-foreground group-hover:translate-x-1 transition-transform">→</span>
      </a>
    ),
    bold: (link, i) => (
      <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
        onClick={() => onLinkClick(i)}
        className="block group" data-testid={`microsite-link-${i}`}>
        <div className="p-4 rounded-2xl bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white hover:scale-[1.02] transition-all flex items-center justify-between mt-3">
          <span className="font-medium">{link.title}</span>
          <ExternalLink className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </div>
      </a>
    ),
  };

  const renderLink = variants[variant] || variants.rounded;

  return (
    <div className={`space-y-3 ${variant === "minimal" ? "space-y-0" : ""}`}>
      {microsite.links.map((link, i) => renderLink(link, i))}
    </div>
  );
};

const Footer = ({ color, light = false }) => (
  <div className="mt-12 text-center">
    <Link to="/"
      className={`inline-flex items-center gap-2 text-sm transition-colors ${light ? "text-white/70 hover:text-white" : "text-muted-foreground hover:text-foreground"}`}>
      <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: color }}>
        <Link2 className="w-4 h-4 text-white" />
      </div>
      <span>Dibuat dengan Campus Link</span>
    </Link>
  </div>
);

// ============ MAIN COMPONENT ============

const PublicMicrosite = () => {
  const { slug } = useParams();
  const { theme, toggleTheme } = useTheme();
  const [microsite, setMicrosite] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { fetchMicrosite(); }, [slug]);

  const fetchMicrosite = async () => {
    try {
      const res = await axios.get(`${API}/m/${slug}`);
      setMicrosite(res.data);
    } catch {
      setError("Microsite tidak ditemukan");
    } finally {
      setIsLoading(false);
    }
  };

  // Track klik per link — kirim ke backend
  const handleLinkClick = async (linkIndex) => {
    if (!microsite) return;
    try {
      await axios.post(`${API}/m/${slug}/click`, { link_index: linkIndex });
    } catch {
      // silent fail — jangan ganggu user
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !microsite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Link2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h1 className="text-2xl font-bold mb-2">404</h1>
          <p className="text-muted-foreground mb-6">{error || "Microsite tidak ditemukan"}</p>
          <Link to="/"><Button>Kembali ke Home</Button></Link>
        </div>
      </div>
    );
  }

  const color = themeMap[microsite.theme] || "#7c3aed";
  const template = microsite.template || "classic";

  const templateProps = { microsite, color, onLinkClick: handleLinkClick };

  // Background wrapper untuk non-bold templates
  const wrapperStyle = template !== "bold" ? {
    background: microsite.background_url
      ? `url(${microsite.background_url}) center/cover fixed`
      : `linear-gradient(180deg, ${color}15 0%, transparent 40%)`
  } : {};

  return (
    <div className="min-h-screen bg-background" style={wrapperStyle} data-testid="public-microsite">
      {/* Theme Toggle */}
      <div className="fixed top-4 right-4 z-50">
        <Button variant="ghost" size="icon" onClick={toggleTheme}
          className="bg-background/80 backdrop-blur-md" data-testid="public-theme-toggle">
          {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </div>

      {/* Render template */}
      {template === "classic"  && <TemplateClassic {...templateProps} />}
      {template === "card"     && <TemplateCard    {...templateProps} />}
      {template === "minimal"  && <TemplateMinimal {...templateProps} />}
      {template === "bold"     && <TemplateBold    {...templateProps} />}
    </div>
  );
};

export default PublicMicrosite;